import React from "react";
import { DetailsPopup } from "modules/common/components/form/DetailsPopup";
import { Block } from "modules/common/components/page/Block";
import { matchRoute } from "modules/common/services/routing";
import { WhenPath } from "modules/common/components/routing/WhenPath";
import { routes } from "modules/common/routes";
import { observer } from "mobx-react";
import { texts } from "modules/common/texts";
import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { OrderStatusStoreType } from "../models/order-status-store";
import { OrderStatusDetails } from "./details/OrderStatusDetails";
import { OrderStatusesList } from "./list/OrderStatusList";
import { menuItems } from "modules/root/components/menu/MainMenu";
import func from "modules/dictionaries/functionalities";
import { can } from "modules/session/auth/access";

import { PlanrButton } from "modules/common/components/planr/button/Button";
import styles from "./OrderStatusPage.module.scss";
import { Search } from "modules/common/components/form/Search";
import { withNavigation, NavigationProps, type Location } from "modules/common/components/routing/withNavigation";

const menu = menuItems();

class OrderStatusPage extends React.Component<OrderStatusPagePageProps & NavigationProps, OrderStatusPageState> {
  constructor(props: OrderStatusPagePageProps & NavigationProps) {
    super(props);

    this.state = { statusId: getSelectedStatusId(props.location.pathname) };
  }

  componentDidMount() {
    document.title = menu.dictionaries.nested.orderStatuses.label;

    const { statusId } = this.state;

    this.loadList();
    this.loadDetails(statusId);
  }

  componentDidUpdate(prevProps: NavigationProps) {
    // смена адреса приходит новым location в пропах
    if (prevProps.location !== this.props.location) {
      this.onLocationChanged(this.props.location);
    }
  }

  onLocationChanged = (location: Location) => {
    const statusId = getSelectedStatusId(location.pathname);

    if (this.state.statusId !== statusId) {
      this.setState({ statusId });
      this.loadDetails(statusId);
    }
  };

  render() {
    const { store, access } = this.props;
    const canChange = can(func.ORDERSTATUS_EDIT, access);

    return (
      <Block columned={true} className={styles.page}>
        <WhenPath path={routes.dictionaries.orderStatus.details()}>
          <DetailsPopup title="Статус договора" onCancel={this.onStatusSelected}>
            <OrderStatusDetails
              canChange={canChange}
              orderStatus={store.details}
              onSaved={this.onStatusSaved}
              onRemoved={this.onStatusRemoved}
            />
          </DetailsPopup>
        </WhenPath>
        <div className={styles.tools}>
          {canChange && (
            <PlanrButton
              type="secondary"
              size="small"
              icon="general-plus-big"
              onClick={this.onStatusAdd}
              style={{
                width: "260px",
                marginBottom: "32px",
                borderRadius: "120px",
              }}
            >{`${texts.add} cтатус договора`}</PlanrButton>
          )}
        </div>
        <div className={styles.mobileTools}>
          {canChange && <PlanrButton type="secondary" round icon="general-plus-big" onClick={this.onStatusAdd} />}
        </div>
        <div className={styles.right}>
          <Search query={store.list.query} onSearch={store.list.setQuery} />
        </div>
        <OrderStatusesList store={store.list} onSelected={this.onStatusSelected} onRemoved={this.onStatusRemoved} />
      </Block>
    );
  }

  onStatusAdd = () => {
    this.onStatusSelected(EMPTY_OBJECT_ID);
  };

  onStatusSelected = (id: string = "") => {
    const { navigate } = this.props;

    const url = id ? routes.dictionaries.orderStatus.details(id) : routes.dictionaries.orderStatus.path;

    navigate(url);
  };

  onStatusSaved = () => {
    this.onStatusSelected("");
    this.loadList();
  };

  onStatusRemoved = () => {
    const { navigate } = this.props;

    this.loadList();

    const url = routes.dictionaries.orderStatus.path;
    navigate(url);
  };

  loadList = () => this.props.store.list.load();

  loadDetails = (id: string) => this.props.store.details.load(id);
}

export default withNavigation(observer(OrderStatusPage));

function getSelectedStatusId(pathname: string) {
  const match = matchRoute<OrderStatusPageState>(pathname, routes.dictionaries.orderStatus.details());

  return match && match.params ? match.params.statusId : "";
}

interface OrderStatusPagePageProps {
  store: OrderStatusStoreType;
  access: string[];
}

interface OrderStatusPageState {
  statusId: string;
}
